import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Switch,
  FormControlLabel,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Backdrop,
} from '@mui/material';
import { Fade } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LockIcon from '@mui/icons-material/Lock';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import Info from './Info';
import { useAdminLanguage } from './AdminI18n';

const PocketAdmin = () => {
  const { t } = useAdminLanguage();
  const [pocketSettings, setPocketSettings] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessMessage, setAccessMessage] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessResponse = await fetch('/api/pocketsettings/access', { headers: getAuthHeaders() });
        if (!accessResponse.ok) throw new Error(`Erreur accès ${accessResponse.status}`);
        const accessData = await accessResponse.json();

        setHasAccess(accessData.hasAccess);
        setAccessMessage(accessData.reason);

        const settingsResponse = await fetch('/api/pocketsettings', { headers: getAuthHeaders() });
        if (!settingsResponse.ok) throw new Error(`Erreur paramètres ${settingsResponse.status}`);
        const settingsData = await settingsResponse.json();

        setPocketSettings(settingsData);
      } catch (err) {
        console.error('Erreur lors de la récupération des données BlueNote Pocket :', err);
        setError('Une erreur est survenue lors du chargement des données BlueNote Pocket.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSwitchChange = async (event) => {
    if (!hasAccess) {
      return;
    }

    const newStatus = event.target.checked;
    setUpdating(true);
    setMessage('');
    setError(null);

    try {
      const response = await fetch('/api/pocketsettings/status', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ pocketOn: newStatus }),
      });

      const data = await response.json();

      if (response.ok) {
        setPocketSettings(data.settings);
        setMessage(`BlueNote Pocket ${newStatus ? 'activé' : 'désactivé'} avec succès.`);
      } else {
        setError(data.error || 'Erreur lors de la mise à jour du statut BlueNote Pocket.');
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour :', err);
      setError('Erreur lors de la communication avec le serveur.');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = () => {
    if (!hasAccess) return 'error';
    return pocketSettings?.pocketOn ? 'success' : 'default';
  };

  const getStatusIcon = () => {
    if (!hasAccess) return <LockIcon />;
    return pocketSettings?.pocketOn ? <CheckCircleIcon /> : <PhoneIphoneIcon />;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in={!loading} timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion de BlueNote Pocket')}
          </Typography>

          <Info type="pocket" />

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              {!hasAccess && (
                <Backdrop
                  open={true}
                  sx={{
                    position: 'absolute',
                    zIndex: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    borderRadius: 1,
                  }}
                />
              )}

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={{
                    opacity: hasAccess ? 1 : 0.5,
                    transition: 'opacity 0.3s'
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusIcon()}
                        {t('Contrôle BlueNote Pocket')}
                      </Typography>

                      <FormControlLabel
                        labelPlacement="start"
                        control={
                          <Switch
                            checked={pocketSettings?.pocketOn || false}
                            onChange={handleSwitchChange}
                            disabled={!hasAccess || updating}
                            color={getStatusColor()}
                          />
                        }
                        label={
                          <Typography sx={{ mr: 2 }}>
                            {pocketSettings?.pocketOn ? t('BlueNote Pocket activé') : t('BlueNote Pocket désactivé')}
                          </Typography>
                        }
                        sx={{
                          mt: 2,
                          width: '100%',
                          m: 0,
                          justifyContent: 'space-between',
                          '& .MuiFormControlLabel-label': { flex: 1 }
                        }}
                      />

                      {updating && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2">{t('Mise à jour en cours...')}</Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {!hasAccess && (
                    <Alert
                      severity="error"
                      sx={{ mt: 2 }}
                      icon={<LockIcon />}
                    >
                      {t(accessMessage)}
                    </Alert>
                  )}

                  {message && (
                    <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
                      {t(message)}
                    </Alert>
                  )}

                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }} icon={<ErrorIcon />}>
                      {t(error)}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{
                    opacity: hasAccess ? 1 : 0.5,
                    transition: 'opacity 0.3s'
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('Informations BlueNote Pocket')}
                      </Typography>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>{t('Statut')} :</strong>{' '}
                          <span style={{
                            color: !hasAccess ? 'red' : (pocketSettings?.pocketOn ? 'green' : 'orange')
                          }}>
                            {!hasAccess
                              ? t('Non autorisé')
                              : (pocketSettings?.pocketOn ? t('Actif') : t('Inactif'))
                            }
                          </span>
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>{t('Accès licence')} :</strong>{' '}
                          <span style={{ color: hasAccess ? 'green' : 'red' }}>
                            {hasAccess ? t('Autorisé') : t('Non autorisé')}
                          </span>
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Fade>
    </Container>
  );
};

export default PocketAdmin;
